import { useState, useEffect, useRef, useCallback } from 'react'

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

// stage の日本語。studio 側(studio/status.py STAGE_LABEL / app/main.gd STAGE_JA)と同じ語を使う。
// 片方だけ変えると同じ状態が2つの名前で呼ばれるので、直すときは全部直すこと。
const STAGE = {
  recording: { label: '録音中/中断', tone: 'bg-amber-500/20 text-amber-300 border-amber-700/50' },
  recorded: { label: '録音済', tone: 'bg-sky-500/20 text-sky-300 border-sky-700/50' },
  composed: { label: '合成済', tone: 'bg-sky-500/20 text-sky-300 border-sky-700/50' },
  published: { label: '書き出し済', tone: 'bg-sky-500/20 text-sky-300 border-sky-700/50' },
  uploaded: { label: '投稿済', tone: 'bg-green-500/20 text-green-400 border-green-700/50' },
}

const bytes = (n) => {
  const v = Number(n)
  if (!Number.isFinite(v)) return '-'
  if (v >= 1e12) return `${(v / 1e12).toFixed(1)} TB`
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)} GB`
  if (v >= 1e6) return `${(v / 1e6).toFixed(0)} MB`
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} KB`
  return `${Math.round(v)} B`
}

// duration_s は**キーが在って値が null** のことがある(まだ尺が測れていない回)。
// 0 と null を混ぜないよう、数値のときだけ描く。
const secs = (n) => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return null
  const s = Math.round(n)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = s % 60
  return h > 0 ? `${h}時間${m}分${String(r).padStart(2, '0')}秒`
    : `${m}分${String(r).padStart(2, '0')}秒`
}

const age = (sec) => {
  if (typeof sec !== 'number') return ''
  if (sec < 60) return `${sec}秒前`
  if (sec < 3600) return `${Math.floor(sec / 60)}分前`
  if (sec < 86400) return `${Math.floor(sec / 3600)}時間前`
  return `${Math.floor(sec / 86400)}日前`
}

function Panel({ title, right, children }) {
  return (
    <section className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-bold text-sm text-gray-300">{title}</h2>
        {right && <span className="text-xs text-gray-500">{right}</span>}
      </div>
      {children}
    </section>
  )
}

// 依頼を出して、結果が返るまで待つ。結果は agent(作業マシン)が書く。
// **待ちを無限にしない** — agent が止まっていることは普通にあるので、諦めて理由を出す。
const POLL_MS = 1200
const GIVE_UP_MS = 90000

export default function StudioInventory() {
  const [state, setState] = useState({ loading: true })
  const [run, setRun] = useState(null)   // {cmd, status, output, ok}
  const timers = useRef([])

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  useEffect(() => clearTimers, [])

  const load = useCallback(() => {
    fetch('/cruz/dev/studio.json', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => setState({ loading: false, ...d }))
      // ネットワーク自体が落ちた場合。**空で黙らない** — 空だと「在庫ゼロ」に見える
      .catch(() => setState({ loading: false, ok: false, message: '通信に失敗しました' }))
  }, [])

  useEffect(() => { load() }, [load])

  function ask(cmd, args = []) {
    clearTimers()
    setRun({ cmd, status: 'requested', output: '' })
    fetch('/cruz/dev/studio/commands', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf(), Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ cmd, args }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) { setRun({ cmd, status: 'done', ok: false, output: d.message || '依頼できませんでした' }); return }
        const started = Date.now()
        const poll = () => {
          fetch(`/cruz/dev/studio/commands/${d.id}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then((r) => r.json())
            .then((res) => {
              if (res.status === 'done') {
                setRun({ cmd, status: 'done', ok: res.ok, output: res.output, command: res.command })
                if (cmd === 'snapshot') load()      // 更新したなら画面も読み直す
              } else if (Date.now() - started > GIVE_UP_MS) {
                setRun({ cmd, status: 'done', ok: false,
                  output: [
                    '作業マシンから結果が返りませんでした。',
                    'E:\\godot\\studio で `studio.bat agent` が動いているか確認してください',
                  ].join('\n') })
              } else {
                timers.current.push(setTimeout(poll, POLL_MS))
              }
            })
            .catch(() => setRun({ cmd, status: 'done', ok: false, output: '結果の取得に失敗しました' }))
        }
        timers.current.push(setTimeout(poll, POLL_MS))
      })
      .catch(() => setRun({ cmd, status: 'done', ok: false, output: '依頼を送れませんでした' }))
  }

  if (state.loading) return <p className="text-gray-500">読み込み中…</p>

  if (!state.ok) {
    return (
      <div className="bg-amber-950/40 border border-amber-800 rounded-xl p-4 text-amber-200">
        <p className="font-bold mb-1">在庫を表示できません</p>
        <p className="text-sm text-amber-300/80">{state.message}</p>
      </div>
    )
  }

  const st = state.status || {}
  const eps = st.episodes || []
  const lib = st.library || {}
  const disks = st.disks || []
  const verify = state.verify || null
  const commands = state.commands || []
  const verdict = (id) => (verify || []).find((v) => v.episode === id)

  const libWarnings = [
    (lib.orphans || []).length && `index に無い実ファイル ${lib.orphans.length} 件`,
    (lib.missing || []).length && `実ファイルが無い index ${lib.missing.length} 件`,
    lib.zero_length && `尺0のクリップ ${lib.zero_length} 本`,
  ].filter(Boolean)

  return (
    <div>
      {state.stale && (
        <p className="mb-4 text-xs text-amber-300/90 bg-amber-950/30 border border-amber-900 rounded px-3 py-2">
          このスナップショットは {age(state.age_seconds)} のものです。
          作業マシンで <code>studio snapshot</code> を実行すると更新されます。
        </p>
      )}

      <Panel
        title={`エピソード ${eps.length} 本`}
        right={`${state.host || ''} ・ ${age(state.age_seconds)}`}
      >
        {eps.length === 0 && <p className="text-gray-500 text-sm">まだありません</p>}
        <ul className="space-y-2">
          {eps.map((e) => {
            const s = STAGE[e.stage] || { label: e.stage, tone: 'bg-gray-700/40 text-gray-300 border-gray-600' }
            const v = verdict(e.id)
            const d = secs(e.duration_s)
            return (
              <li key={e.id} className="flex items-center gap-3 flex-wrap text-sm">
                <span className="font-mono font-bold w-16">{e.id}</span>
                <span className={`px-2 py-0.5 rounded border text-xs ${s.tone}`}>{s.label}</span>
                {d && <span className="text-gray-400 tabular-nums">{d}</span>}
                {v && (
                  <span className={v.bad ? 'text-red-400 text-xs' : v.warn ? 'text-amber-400 text-xs' : 'text-green-400 text-xs'}>
                    {v.bad ? `verify 要修正 ${v.bad}` : v.warn ? `verify 注意 ${v.warn}` : 'verify OK'}
                  </span>
                )}
                <span className="text-gray-500 text-xs ml-auto truncate max-w-[52%]" title={e.next}>
                  {e.youtube_url
                    ? <a href={e.youtube_url} className="hover:text-gray-300 underline" target="_blank" rel="noreferrer">{e.youtube_url}</a>
                    : e.next}
                </span>
              </li>
            )
          })}
        </ul>
      </Panel>

      <Panel title="作業マシンで実行" right="録音・書き出し・投稿は対象外（手元でどうぞ）">
        {commands.length === 0 ? (
          <p className="text-gray-500 text-sm">
            studio が実行できるコマンドを広告していません（古いスナップショットかもしれません）
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {commands.map((c) => (
              <button
                key={c.cmd}
                type="button"
                disabled={run?.status === 'requested'}
                onClick={() => ask(c.cmd, c.flags.includes('--all') ? ['--all'] : [])}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-700 bg-gray-800
                           hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                title={`studio ${c.cmd}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {run && (
          <div className="mt-3">
            <p className="text-xs text-gray-500 mb-1">
              {run.status === 'requested'
                ? `studio ${run.cmd} を作業マシンに頼みました…`
                : (run.command || `studio ${run.cmd}`)}
              {run.status === 'done' && (
                <span className={run.ok ? ' text-green-400' : ' text-red-400'}>
                  {run.ok ? ' ✓' : ' ✕'}
                </span>
              )}
            </p>
            {run.output && (
              <pre className="bg-gray-950 border border-gray-800 rounded p-3 text-xs
                              overflow-x-auto max-h-80 whitespace-pre-wrap">{run.output}</pre>
            )}
          </div>
        )}
      </Panel>

      <Panel title="素材">
        <p className="text-sm">
          <span className="font-bold">{lib.count ?? 0}</span> 本
          <span className="text-gray-500"> ・ 合計 {secs(lib.total_s) || '0分00秒'} ・ 未使用 {lib.unused ?? 0} 本</span>
        </p>
        {libWarnings.length > 0 && (
          <p className="mt-2 text-xs text-amber-400">
            ⚠ {libWarnings.join(' / ')} → 作業マシンで <code>studio clip --repair</code>
          </p>
        )}
      </Panel>

      <Panel
        title="ディスク"
        right={st.cleanable_bytes > 0 ? `${bytes(st.cleanable_bytes)} 返せます（studio clean）` : null}
      >
        <ul className="space-y-1 text-sm">
          {disks.map((d) => (
            <li key={d.name} className="flex items-center gap-3">
              <span className="w-16 text-gray-400">{d.name}</span>
              <span className="tabular-nums">{bytes(d.used_bytes)} 使用</span>
              <span className="text-gray-500 tabular-nums">空き {bytes(d.free_bytes)}</span>
              <span className="text-gray-600 text-xs ml-auto font-mono truncate max-w-[45%]">{d.path}</span>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  )
}
